import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, Check, FileText } from 'lucide-react';

// Service detail data
const serviceDetails = {
  'agency-underwriting': {
    title: 'Agency Underwriting Support',
    subtitle: 'Live Collaboration with Your Team',
    description: 'See how Integrand Analytics underwriting support team collaborates with agency lenders in a live environment. Real examples of populating Fannie Mae and Freddie Mac workbooks — from request to delivery.',
    category: 'Loan Origination & Servicing',
    heroImage: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80',
    overview: 'Each deal typically involves populating multiple Excel workbook tabs from third-party reports — appraisals, PCAs, ESAs, zoning, and financial statements. Our expert team handles all the data extraction and population while you focus on deal analysis.',
    workbookTabs: [
      { name: 'IE (Income/Expense)', desc: 'T-12, annual & monthly P&L spreads from borrower financials' },
      { name: 'Market Analysis', desc: 'Market data from appraisals and REIS reports' },
      { name: 'Rent Comps', desc: 'Comparable rental data from appraisal comparables' },
      { name: 'Rent Roll', desc: 'Unit-level tenant data, lease dates, SF, and charges' },
      { name: 'PNA (Physical Needs)', desc: 'Capital expenditure data from PCA reports' },
      { name: 'ESA (Environmental)', desc: 'Environmental Site Assessment summaries' },
      { name: 'Zoning', desc: 'Zoning compliance from third-party zoning reports' },
      { name: 'Appraisal Tab', desc: 'Key appraisal data points and valuation summaries' },
      { name: 'Expense Analysis', desc: "Borrower's and appraiser's expense comparables" },
      { name: 'Sales Comps', desc: 'Comparable sales data for valuation analysis' },
      { name: 'Repair Table', desc: 'Required repairs from property condition assessments' },
      { name: 'Narrative Tabs', desc: 'Property manager interviews, market write-ups' }
    ],
    process: [
      { step: 1, title: 'Analyst emails request', desc: 'Your team sends workbook tab requests specifying source documents' },
      { step: 2, title: 'Files shared via Teams', desc: 'Documents uploaded to dedicated deal channels' },
      { step: 3, title: 'Integrand processes data', desc: 'Our experts extract and populate all required tabs' },
      { step: 4, title: 'Completed workbook delivered', desc: 'Quality-checked deliverable returned within hours' }
    ],
    benefits: [
      'Same-day turnaround on standard requests',
      'Direct integration with your existing workflow',
      'Dedicated team familiar with your templates',
      '99%+ accuracy with multi-level QC',
      'Scalable capacity for deal volume spikes',
      'Proactive flagging of data discrepancies'
    ],
    relatedServices: ['financial-model', 'disbursement-support', 'loan-due-diligence']
  },
  'financial-model': {
    title: 'Custom Financial Model Development',
    subtitle: 'Bespoke Models Built by CRE Experts',
    description: 'Excel-based financial models tailored to your specific deal requirements. Our CRE experts create custom models for acquisition analysis, development projects, and complex structured transactions.',
    category: 'Loan Origination & Servicing',
    heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80',
    overview: 'Every deal is unique. Our financial modeling team creates bespoke Excel models that match your specific analysis requirements, whether it\'s a standard acquisition model or a complex waterfall structure.',
    workbookTabs: [
      { name: 'Cash Flow Projections', desc: 'Detailed monthly and annual cash flow forecasts' },
      { name: 'Sensitivity Analysis', desc: 'Multiple scenario testing with key variable adjustments' },
      { name: 'Return Metrics', desc: 'IRR, equity multiple, cash-on-cash calculations' },
      { name: 'Debt Sizing', desc: 'Loan sizing based on DSCR and LTV constraints' },
      { name: 'Waterfall Analysis', desc: 'Complex promote and distribution structures' },
      { name: 'Exit Analysis', desc: 'Disposition scenarios and hold period optimization' }
    ],
    process: [
      { step: 1, title: 'Scope discussion', desc: 'We understand your specific modeling requirements' },
      { step: 2, title: 'Template development', desc: 'Custom model built to your specifications' },
      { step: 3, title: 'Review & iteration', desc: 'Refinements based on your feedback' },
      { step: 4, title: 'Final delivery', desc: 'Production-ready model with documentation' }
    ],
    benefits: [
      'Models built by experienced CRE analysts',
      'Flexible formatting to match your standards',
      'Full audit trail and formula transparency',
      'Ongoing support for model updates',
      'Quick turnaround for urgent deals',
      'Confidential handling of all deal data'
    ],
    relatedServices: ['agency-underwriting', 'disbursement-support', 'loan-due-diligence']
  },
  'disbursement-support': {
    title: 'Disbursement & PCNA Support',
    subtitle: 'Accurate Processing with Expert Oversight',
    description: 'Comprehensive loan disbursement processing and PCNA reporting services. Our team ensures accurate documentation and timely processing for your loan servicing operations.',
    category: 'Loan Origination & Servicing',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80',
    overview: 'Loan disbursement and PCNA reporting require meticulous attention to detail. Our experienced team handles the entire process from document review to final reporting, ensuring compliance and accuracy.',
    workbookTabs: [
      { name: 'Disbursement Tracking', desc: 'Real-time status monitoring for all disbursement requests' },
      { name: 'PCNA Reports', desc: 'Property Condition and Needs Assessment documentation' },
      { name: 'Compliance Checks', desc: 'Verification against loan documents and requirements' },
      { name: 'Documentation', desc: 'Complete audit trail for all transactions' }
    ],
    process: [
      { step: 1, title: 'Request received', desc: 'Disbursement or PCNA request submitted' },
      { step: 2, title: 'Document review', desc: 'Verification of all supporting documentation' },
      { step: 3, title: 'Processing', desc: 'Data extraction and report generation' },
      { step: 4, title: 'Quality review & delivery', desc: 'Final QC and submission' }
    ],
    benefits: [
      'Dedicated disbursement specialists',
      'Same-day processing for urgent requests',
      'Complete compliance documentation',
      'Integration with your servicing platform',
      'Detailed status reporting',
      'Scalable for portfolio growth'
    ],
    relatedServices: ['agency-underwriting', 'financial-model', 'loan-due-diligence']
  },
  'loan-due-diligence': {
    title: 'Loan Due Diligence',
    subtitle: 'Comprehensive Analysis for Confident Decisions',
    description: 'Thorough due diligence services for commercial mortgage loans and CMBS transactions. Our team provides detailed analysis to support your investment decisions.',
    category: 'Loan Origination & Servicing',
    heroImage: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
    overview: 'Due diligence is critical for sound lending decisions. Our experienced analysts review all aspects of the transaction, identifying risks and providing actionable insights.',
    workbookTabs: [
      { name: 'Document Review', desc: 'Comprehensive review of all loan documents' },
      { name: 'Risk Assessment', desc: 'Identification and analysis of key risk factors' },
      { name: 'Compliance Verification', desc: 'Regulatory and policy compliance checks' },
      { name: 'Exception Reporting', desc: 'Clear documentation of findings and exceptions' }
    ],
    process: [
      { step: 1, title: 'Scope definition', desc: 'Define due diligence requirements and timeline' },
      { step: 2, title: 'Document collection', desc: 'Gather all required documents and data' },
      { step: 3, title: 'Analysis', desc: 'Detailed review and risk assessment' },
      { step: 4, title: 'Report delivery', desc: 'Comprehensive findings report' }
    ],
    benefits: [
      'Experienced due diligence analysts',
      'Customizable scope and depth',
      'Fast turnaround for time-sensitive deals',
      'Clear, actionable reporting',
      'Scalable for large portfolios',
      'Confidential data handling'
    ],
    relatedServices: ['agency-underwriting', 'financial-model', 'disbursement-support']
  },
  'lease-abstraction': {
    title: 'Lease Abstraction & Audit',
    subtitle: 'Expert-Driven Data Extraction',
    description: 'Extract and audit critical lease data with expert-driven review and validation. Our team handles complex lease documents across all property types.',
    category: 'Lease & Data Administration',
    heroImage: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80',
    overview: 'Lease abstraction requires deep understanding of commercial lease structures. Our team extracts 50+ data points per lease, ensuring accuracy through multiple quality checkpoints.',
    workbookTabs: [
      { name: 'Key Terms', desc: 'Base rent, escalations, options, and critical dates' },
      { name: 'Tenant Information', desc: 'Tenant details, guarantors, and contact information' },
      { name: 'Financial Terms', desc: 'Rent schedules, CAM, taxes, and recoveries' },
      { name: 'Options & Rights', desc: 'Renewal options, purchase rights, and ROFR' },
      { name: 'Operating Provisions', desc: 'Use restrictions, exclusives, and co-tenancy' },
      { name: 'Insurance & Indemnity', desc: 'Coverage requirements and liability provisions' }
    ],
    process: [
      { step: 1, title: 'Document receipt', desc: 'Lease documents uploaded securely' },
      { step: 2, title: 'Abstraction', desc: 'Data extraction by trained specialists' },
      { step: 3, title: 'Quality review', desc: 'Multi-level QC for accuracy' },
      { step: 4, title: 'Delivery', desc: 'Abstracts in your preferred format' }
    ],
    benefits: [
      '50+ data points per lease',
      '98%+ accuracy rate',
      'All property types supported',
      'Scalable for large portfolios',
      'OCR processing for scanned documents',
      'Integration with your lease admin system'
    ],
    relatedServices: ['loan-onboarding', 'cam-reconciliations', 'lease-admin-support']
  },
  'loan-onboarding': {
    title: 'Loan Onboarding & Data Digitization',
    subtitle: 'Convert Legacy Data to Structured Records',
    description: 'Transform legacy loan data from documents into structured, reliable records. Our team handles bulk conversions and system migrations with precision.',
    category: 'Lease & Data Administration',
    heroImage: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&q=80',
    overview: 'Legacy data trapped in documents limits your operational efficiency. We convert paper records and PDFs into structured data that powers your systems and analytics.',
    workbookTabs: [
      { name: 'Loan Terms', desc: 'Principal, rate, maturity, and payment terms' },
      { name: 'Collateral Data', desc: 'Property details and valuations' },
      { name: 'Borrower Information', desc: 'Entity structure and contact details' },
      { name: 'Payment History', desc: 'Historical payment records and status' }
    ],
    process: [
      { step: 1, title: 'Scope assessment', desc: 'Evaluate document volume and complexity' },
      { step: 2, title: 'Template design', desc: 'Create data capture templates' },
      { step: 3, title: 'Bulk processing', desc: 'Extract and validate data' },
      { step: 4, title: 'System upload', desc: 'Load data to your platform' }
    ],
    benefits: [
      'Bulk processing capability',
      'Custom data mapping',
      'Quality assurance at every step',
      'Secure document handling',
      'System integration support',
      'Ongoing data maintenance'
    ],
    relatedServices: ['lease-abstraction', 'cam-reconciliations', 'lease-admin-support']
  },
  'cam-reconciliations': {
    title: 'CAM Reconciliations',
    subtitle: 'Expert Recovery Audit Services',
    description: 'Common area maintenance reconciliation and recovery audit services. We ensure accurate expense allocation and maximize your recovery potential.',
    category: 'Lease & Data Administration',
    heroImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80',
    overview: 'CAM reconciliation is complex and time-consuming. Our specialists handle the entire process, from expense verification to tenant billing, ensuring compliance with lease terms.',
    workbookTabs: [
      { name: 'Expense Verification', desc: 'Review and categorize all recoverable expenses' },
      { name: 'Pro-rata Calculations', desc: 'Accurate tenant share calculations' },
      { name: 'Billing Statements', desc: 'Professional tenant reconciliation statements' },
      { name: 'Audit Support', desc: 'Documentation for tenant audits' }
    ],
    process: [
      { step: 1, title: 'Data collection', desc: 'Gather expense data and lease terms' },
      { step: 2, title: 'Analysis', desc: 'Verify expenses and calculate shares' },
      { step: 3, title: 'Statement preparation', desc: 'Create tenant billing statements' },
      { step: 4, title: 'Support', desc: 'Handle tenant questions and audits' }
    ],
    benefits: [
      'Maximize recovery potential',
      'Lease-compliant calculations',
      'Professional billing statements',
      'Tenant audit support',
      'Year-end and mid-year reconciliations',
      'Portfolio-wide consistency'
    ],
    relatedServices: ['lease-abstraction', 'loan-onboarding', 'lease-admin-support']
  },
  'lease-admin-support': {
    title: 'Lease Administration Support',
    subtitle: 'Comprehensive Portfolio Management',
    description: 'Full-service lease administration including critical date tracking, rent roll maintenance, and compliance monitoring for your entire portfolio.',
    category: 'Lease & Data Administration',
    heroImage: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
    overview: 'Effective lease administration requires constant attention to deadlines and details. Our team provides ongoing support to keep your portfolio running smoothly.',
    workbookTabs: [
      { name: 'Critical Dates', desc: 'Expirations, options, and notice deadlines' },
      { name: 'Rent Roll', desc: 'Current rent roll with all charges' },
      { name: 'Escalations', desc: 'Track and apply rent increases' },
      { name: 'Options', desc: 'Monitor and exercise lease options' }
    ],
    process: [
      { step: 1, title: 'Portfolio setup', desc: 'Load your leases and establish workflows' },
      { step: 2, title: 'Ongoing monitoring', desc: 'Track all critical dates and deadlines' },
      { step: 3, title: 'Proactive alerts', desc: 'Notify you of upcoming actions' },
      { step: 4, title: 'Execution support', desc: 'Help execute options and renewals' }
    ],
    benefits: [
      'Never miss a critical date',
      'Accurate rent roll maintenance',
      'Escalation tracking and application',
      'Option exercise support',
      'Tenant correspondence management',
      'Portfolio reporting and analytics'
    ],
    relatedServices: ['lease-abstraction', 'loan-onboarding', 'cam-reconciliations']
  },
  'salesforce-consulting': {
    title: 'Salesforce Customization & Integration',
    subtitle: 'Tailored CRM Solutions for CRE',
    description: 'Configure and integrate Salesforce to match your commercial real estate business processes. Our team delivers customizations that drive efficiency.',
    category: 'Consulting & Tech Solutions',
    heroImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80',
    overview: 'Salesforce is powerful, but only when configured for your specific workflows. We customize the platform to match how your CRE business actually operates.',
    workbookTabs: [
      { name: 'Custom Objects', desc: 'Properties, deals, and relationships' },
      { name: 'Workflows', desc: 'Automated processes and approvals' },
      { name: 'Integrations', desc: 'Connect with your other systems' },
      { name: 'Reports & Dashboards', desc: 'Real-time visibility into your pipeline' }
    ],
    process: [
      { step: 1, title: 'Discovery', desc: 'Understand your processes and pain points' },
      { step: 2, title: 'Design', desc: 'Create solution architecture' },
      { step: 3, title: 'Build', desc: 'Configure and customize Salesforce' },
      { step: 4, title: 'Deploy & train', desc: 'Go live with user training' }
    ],
    benefits: [
      'CRE-specific expertise',
      'Streamlined deal tracking',
      'Improved team collaboration',
      'Better pipeline visibility',
      'Automated workflows',
      'Ongoing support and optimization'
    ],
    relatedServices: ['reporting-dashboards', 'cre-tech-consulting', 'argus-modeling']
  },
  'reporting-dashboards': {
    title: 'Custom Reporting & Dashboards',
    subtitle: 'Real-Time Visibility Into Your KPIs',
    description: 'Dynamic, customized dashboards tailored to your specific CRE key performance indicators. Get the insights you need to make better decisions.',
    category: 'Consulting & Tech Solutions',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80',
    overview: 'Standard reports don\'t cut it for CRE operations. We build custom dashboards that show exactly what you need to see, updated in real-time.',
    workbookTabs: [
      { name: 'KPI Visualization', desc: 'Key metrics at a glance' },
      { name: 'Trend Analysis', desc: 'Performance over time' },
      { name: 'Drill-down Reports', desc: 'Detailed data exploration' },
      { name: 'Automated Distribution', desc: 'Scheduled report delivery' }
    ],
    process: [
      { step: 1, title: 'Requirements', desc: 'Define your KPIs and reporting needs' },
      { step: 2, title: 'Data mapping', desc: 'Identify data sources and connections' },
      { step: 3, title: 'Dashboard build', desc: 'Create interactive visualizations' },
      { step: 4, title: 'Deployment', desc: 'Launch with training and documentation' }
    ],
    benefits: [
      'Real-time data visibility',
      'Custom KPI tracking',
      'Interactive drill-downs',
      'Automated report scheduling',
      'Mobile-friendly access',
      'Easy self-service updates'
    ],
    relatedServices: ['salesforce-consulting', 'cre-tech-consulting', 'argus-modeling']
  },
  'cre-tech-consulting': {
    title: 'CRE Technology Consulting',
    subtitle: 'Strategic Guidance for Digital Transformation',
    description: 'Expert guidance on CRE technology adoption, implementation, and optimization. We help you navigate the complex landscape of PropTech solutions.',
    category: 'Consulting & Tech Solutions',
    heroImage: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1200&q=80',
    overview: 'The CRE technology landscape is overwhelming. We help you evaluate options, select the right solutions, and implement them successfully.',
    workbookTabs: [
      { name: 'Technology Assessment', desc: 'Evaluate your current tech stack' },
      { name: 'Vendor Selection', desc: 'Compare and select the right solutions' },
      { name: 'Implementation Planning', desc: 'Roadmap for successful deployment' },
      { name: 'Change Management', desc: 'Drive adoption across your organization' }
    ],
    process: [
      { step: 1, title: 'Assessment', desc: 'Evaluate current state and needs' },
      { step: 2, title: 'Strategy', desc: 'Develop technology roadmap' },
      { step: 3, title: 'Selection', desc: 'Vendor evaluation and selection' },
      { step: 4, title: 'Implementation', desc: 'Deployment and change management' }
    ],
    benefits: [
      'Vendor-agnostic advice',
      'CRE-specific expertise',
      'Reduced implementation risk',
      'Faster time to value',
      'Improved user adoption',
      'Ongoing optimization support'
    ],
    relatedServices: ['salesforce-consulting', 'reporting-dashboards', 'argus-modeling']
  },
  'argus-modeling': {
    title: 'ARGUS Modeling Services',
    subtitle: 'Professional Cash Flow Analysis & Valuation',
    description: 'Expert ARGUS Enterprise cash flow modeling and valuation analysis for commercial properties across all asset classes. Our certified analysts deliver institutional-quality models used by lenders, investors, and appraisers nationwide.',
    category: 'Consulting & Tech Solutions',
    heroImage: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
    overview: 'ARGUS Enterprise is the industry standard for commercial real estate valuation and cash flow analysis. Our team of certified ARGUS analysts builds institutional-grade models that support acquisition decisions, loan underwriting, portfolio valuations, and investor reporting. We handle everything from rent roll setup to complex waterfall structures — delivering accurate, audit-ready models on tight timelines.',
    workbookTabs: [
      { name: 'Cash Flow Projections', desc: '10-year DCF with monthly or annual granularity' },
      { name: 'Rent Roll Setup', desc: 'Lease-by-lease data entry with escalations and options' },
      { name: 'Market Leasing', desc: 'Absorption, market rent growth, and renewal assumptions' },
      { name: 'Expense Modeling', desc: 'Recoverable and non-recoverable operating expenses' },
      { name: 'Valuation Analysis', desc: 'Direct cap, DCF, and terminal cap rate scenarios' },
      { name: 'Sensitivity Scenarios', desc: 'Multiple scenario testing with key variable adjustments' }
    ],
    process: [
      { step: 1, title: 'Data collection', desc: 'Gather rent rolls, operating statements, lease abstracts, and market assumptions' },
      { step: 2, title: 'Model build', desc: 'Build ARGUS model with lease-by-lease inputs, expenses, and valuation parameters' },
      { step: 3, title: 'QC & review', desc: 'Multi-level quality check against source documents and market benchmarks' },
      { step: 4, title: 'Delivery & support', desc: 'Deliver ARGUS file, cash flow summary, and valuation reports with ongoing revision support' }
    ],
    benefits: [
      'Certified ARGUS Enterprise analysts',
      'All asset classes — office, retail, industrial, multifamily, mixed-use',
      'Institutional-quality models for lenders and investors',
      'Same-day turnaround for standard models',
      'Model audit and review services for existing files',
      'Training and support for your in-house team'
    ],
    relatedServices: ['salesforce-consulting', 'reporting-dashboards', 'cre-tech-consulting']
  }
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = serviceDetails[slug];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#005B63]/5 to-transparent" />
          <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#005B63]/5 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#005B63] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Services</span>
          </Link>

          {/* Category Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-[#005B63]/10 rounded-full text-[#005B63] text-sm font-medium mb-6">
            {service.category}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {service.title}
          </h1>
          
          <p className="text-xl text-[#005B63] font-medium mb-6">
            {service.subtitle}
          </p>

          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mb-8 leading-relaxed">
            {service.description}
          </p>

          {/* Hero Image */}
          <div className="relative h-64 sm:h-80 lg:h-[400px] overflow-hidden rounded-2xl shadow-lg">
            <img
              src={service.heroImage}
              alt={service.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Support
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {service.overview}
              </p>
              
              {/* Benefits List */}
              <div className="space-y-3">
                {service.benefits.slice(0, 4).map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#005B63] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Workbook Tabs Grid */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#005B63]" />
                What We Handle
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {service.workbookTabs.slice(0, 6).map((tab, index) => (
                  <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 hover:border-[#005B63]/50 hover:shadow-md transition-all">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">{tab.name}</h4>
                    <p className="text-gray-500 text-xs">{tab.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Request → Process → Deliver
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {service.process.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector Line */}
                {index < service.process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-[#005B63] to-[#005B63]/20 -translate-x-1/2 z-0" />
                )}
                
                <div className="relative z-10 bg-white p-6 rounded-2xl border border-gray-200 hover:border-[#005B63]/50 hover:shadow-lg transition-all h-full">
                  <div className="w-12 h-12 bg-[#005B63] text-white rounded-xl flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Integrand
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-gray-50 rounded-2xl hover:shadow-md transition-all">
                <div className="w-8 h-8 bg-[#005B63]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[#005B63]" />
                </div>
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#005B63]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to scale your capacity?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Our team integrates directly into your existing workflow — email, Teams, and your templates. No disruption, immediate capacity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#005B63] rounded-lg font-medium hover:bg-gray-100 transition-colors group">
              Get Started
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
            >
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
